// Look at ./NodeTrackingReports for a working example
import {Button, ButtonGroup} from '@mui/material';
import update from 'immutability-helper';
import isEmpty from 'lodash/isEmpty';
import {useMemo} from 'react';

import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {DashboardPanelActionsSlot} from '../DashboardPanelActionsSlot';
import {
  AMSLocationHistoryReport,
  AMSLocationHistoryReportData,
  getAMSLocationHistoryReportData,
} from './AMSLocationHistoryReport';
import {
  AMSLongTermHistoryReport,
  AMSLongTermHistoryReportData,
  getAMSLongTermHistoryReportData,
} from './AMSLongTermHistoryReport';
import {
  AMSShortTermHistoryReport,
  AMSShortTermHistoryReportData,
  getAMSShortTermHistoryReportData,
} from './AMSShortTermHistoryReport';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export interface GasMonitoringReportsData {
  viewType: 'amsShortTerm' | 'amsLongTerm' | 'amsLocation';
  title: string;
  activeId?: string;
  openedItems: string[];
  amsShortTerm: AMSShortTermHistoryReportData;
  amsLongTerm: AMSLongTermHistoryReportData;
  amsLocation: AMSLocationHistoryReportData;
}

const getGasMonitoringReportsData = (): GasMonitoringReportsData => ({
  viewType: 'amsShortTerm',
  title: 'Gas Monitoring Short Term History',
  activeId: undefined,
  openedItems: [],
  amsShortTerm: getAMSShortTermHistoryReportData(),
  amsLongTerm: getAMSLongTermHistoryReportData(),
  amsLocation: getAMSLocationHistoryReportData(),
});

const getAMSShortTermHistoryReportPanelData = () => ({
  activeId: undefined,
  openedItems: [],
  tabData: {},
});

export const AMSShortTermHistoryReportPanel = ({value, onUpdate}: Props) => {
  const config = useMemo(
    () =>
      !isEmpty(value)
        ? value
        : getAMSShortTermHistoryReportPanelData(),
    [value]
  );
  return (
    <AMSShortTermHistoryReport
      activeId={config.activeId}
      openedItems={config.openedItems}
      tabData={config.tabData}
      onUpdate={(updates) => {
        const updateSpec: any = {};
        if ('activeId' in updates) {
          updateSpec.activeId = {$set: updates.activeId};
        }
        if (updates.openedItems !== undefined) {
          updateSpec.openedItems = {$set: updates.openedItems};
          if (updates.openedItems.length === 0) {
            updateSpec.tabData = {$set: {}};
          }
        }
        if (updates.tabData !== undefined) {
          updateSpec.tabData = {$set: updates.tabData};
        }
        onUpdate?.(update(config, updateSpec));
      }}
    />
  );
};

const getAMSLongTermHistoryReportPanelData = () => ({
  activeId: undefined,
  openedItems: [],
  tabData: {},
});

export const AMSLongTermHistoryReportPanel = ({value, onUpdate}: Props) => {
  const config = useMemo(
    () =>
      !isEmpty(value)
        ? value
        : getAMSLongTermHistoryReportPanelData(),
    [value]
  );
  return (
    <AMSLongTermHistoryReport
      activeId={config.activeId}
      openedItems={config.openedItems}
      tabData={config.tabData}
      onUpdate={(updates) => {
        const updateSpec: any = {};
        if ('activeId' in updates) {
          updateSpec.activeId = {$set: updates.activeId};
        }
        if (updates.openedItems !== undefined) {
          updateSpec.openedItems = {$set: updates.openedItems};
          if (updates.openedItems.length === 0) {
            updateSpec.tabData = {$set: {}};
          }
        }
        if (updates.tabData !== undefined) {
          updateSpec.tabData = {$set: updates.tabData};
        }
        onUpdate?.(update(config, updateSpec));
      }}
    />
  );
};

const getAMSLocationHistoryReportPanelData = () => ({
  activeId: undefined,
  openedItems: [],
  tabData: {},
  mapLayers: ['mine', 'ams_location'],
  mapLevel: 0,
});

export const AMSLocationHistoryReportPanel = ({value, onUpdate}: Props) => {
  const config = useMemo(
    () =>
      !isEmpty(value)
        ? value
        : getAMSLocationHistoryReportPanelData(),
    [value]
  );
  return (
    <AMSLocationHistoryReport
      activeId={config.activeId}
      openedItems={config.openedItems}
      tabData={config.tabData}
      mapLayers={config.mapLayers}
      mapLevel={config.mapLevel}
      onUpdate={(updates) => {
        const updateSpec: any = {};
        if ('activeId' in updates) {
          updateSpec.activeId = {$set: updates.activeId};
        }
        if (updates.openedItems !== undefined) {
          updateSpec.openedItems = {$set: updates.openedItems};
          if (updates.openedItems.length === 0) {
            updateSpec.tabData = {$set: {}};
          }
        }
        if (updates.tabData !== undefined) {
          updateSpec.tabData = {$set: updates.tabData};
        }
        if (updates.mapLayers !== undefined) {
          updateSpec.mapLayers = {$set: updates.mapLayers};
        }
        if (updates.mapLevel !== undefined) {
          updateSpec.mapLevel = {$set: updates.mapLevel};
        }
        onUpdate?.(update(config, updateSpec));
      }}
    />
  );
};

export const GasMonitoringReport = ({value, onUpdate}: Props) => {
  const config = useMemo(
    () =>
      !isEmpty(value)
        ? (value as GasMonitoringReportsData)
        : getGasMonitoringReportsData(),
    [value]
  );
  return (
    <>
      <DashboardPanelActionsSlot>
        <ButtonGroup sx={{minWidth: 410}}>
          <Button
            size="small"
            variant={
              config.viewType === 'amsShortTerm' ? 'contained' : 'outlined'
            }
            onClick={() => {
              onUpdate?.(
                update(config, {
                  viewType: {
                    $set: 'amsShortTerm',
                  },
                })
              );
            }}
          >
            Short Term
          </Button>
          <Button
            size="small"
            variant={
              config.viewType === 'amsLongTerm' ? 'contained' : 'outlined'
            }
            onClick={() => {
              onUpdate?.(
                update(config, {
                  viewType: {
                    $set: 'amsLongTerm',
                  },
                })
              );
            }}
          >
            Long Term
          </Button>
          <Button
            size="small"
            variant={
              config.viewType === 'amsLocation' ? 'contained' : 'outlined'
            }
            onClick={() => {
              onUpdate?.(
                update(config, {
                  viewType: {
                    $set: 'amsLocation',
                  },
                })
              );
            }}
          >
            Location
          </Button>
        </ButtonGroup>
      </DashboardPanelActionsSlot>

      {config.viewType === 'amsShortTerm' ? (
        <AMSShortTermHistoryReport
          activeId={config.activeId}
          openedItems={config.openedItems}
          tabData={config.amsShortTerm.tabs}
          onUpdate={(updates) => {
            const updateSpec: any = {};
            if ('activeId' in updates) {
              updateSpec.activeId = {$set: updates.activeId};
            }
            if (updates.openedItems !== undefined) {
              updateSpec.openedItems = {$set: updates.openedItems};
              if (updates.openedItems.length === 0) {
                updateSpec.amsShortTerm = {tabs: {$set: {}}};
                updateSpec.amsLongTerm = {tabs: {$set: {}}};
                updateSpec.amsLocation = {tabs: {$set: {}}};
              }
            }
            if (updates.tabData !== undefined) {
              updateSpec.amsShortTerm = {tabs: {$set: updates.tabData}};
            }
            onUpdate?.(update(config, updateSpec));
          }}
        />
      ) : config.viewType === 'amsLongTerm' ? (
        <AMSLongTermHistoryReport
          activeId={config.activeId}
          openedItems={config.openedItems}
          tabData={config.amsLongTerm.tabs}
          onUpdate={(updates) => {
            const updateSpec: any = {};
            if ('activeId' in updates) {
              updateSpec.activeId = {$set: updates.activeId};
            }
            if (updates.openedItems !== undefined) {
              updateSpec.openedItems = {$set: updates.openedItems};
              if (updates.openedItems.length === 0) {
                updateSpec.amsShortTerm = {tabs: {$set: {}}};
                updateSpec.amsLongTerm = {tabs: {$set: {}}};
                updateSpec.amsLocation = {tabs: {$set: {}}};
              }
            }
            if (updates.tabData !== undefined) {
              updateSpec.amsLongTerm = {tabs: {$set: updates.tabData}};
            }
            onUpdate?.(update(config, updateSpec));
          }}
        />
      ) : config.viewType === 'amsLocation' ? (
        <AMSLocationHistoryReport
          activeId={config.activeId}
          openedItems={config.openedItems}
          tabData={config.amsLocation.tabs}
          mapLayers={config.amsLocation.mapLayers}
          mapLevel={config.amsLocation.mapLevel}
          onUpdate={(updates) => {
            const updateSpec: any = {};
            if ('activeId' in updates) {
              updateSpec.activeId = {$set: updates.activeId};
            }
            if (updates.openedItems !== undefined) {
              updateSpec.openedItems = {$set: updates.openedItems};
              if (updates.openedItems.length === 0) {
                updateSpec.amsShortTerm = {tabs: {$set: {}}};
                updateSpec.amsLongTerm = {tabs: {$set: {}}};
                updateSpec.amsLocation = {tabs: {$set: {}}};
              }
            }
            if (updates.tabData !== undefined) {
              updateSpec.amsLocation = {tabs: {$set: updates.tabData}};
            }
            if (updates.mapLayers !== undefined) {
              if (!updateSpec.amsLocation) {
                updateSpec.amsLocation = {};
              }
              updateSpec.amsLocation.mapLayers = {$set: updates.mapLayers};
            }
            if (updates.mapLevel !== undefined) {
              if (!updateSpec.amsLocation) {
                updateSpec.amsLocation = {};
              }
              updateSpec.amsLocation.mapLevel = {$set: updates.mapLevel};
            }
            onUpdate?.(update(config, updateSpec));
          }}
        />
      ) : null}
    </>
  );
};
